import React from 'react';
import { type ColumnDef } from '@/components/organism/Table';
import { Cell } from '@/components/atoms';
import { MMS_SALES_PAGE_UI_PROPS } from '@/constant/mmsSales/mmsSales.constants';
import { tid } from '@/utils/testId';
import { MMS_SALES_TEST_IDS as TID } from '@/constant/testIds/mmsSales.testIds';
import { getDateTimeAgo, getDateTime } from '@/utils/dateTime';
import { IMmsSalesProps } from './mmsSales.interface';

export const buildColumns = (
  isCopy: string,
  onCopyClick: (key: string) => void,
): ColumnDef<IMmsSalesProps>[] => [
    {
      accessor: 'sourceOperatorId',
      header: MMS_SALES_PAGE_UI_PROPS.COLUMNS.OPERATOR,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_OPERATOR(`${idx}`))}
          value={item?.sourceOperatorId}
          tooltip
          variant="primary"
          copy={{
            copyKey: `operator-${item?.sourceOrderId}`,
            isCopy,
            onCopyClick,
          }}
          className="w-[150px] text-[12px]"
        />
      ),
    },
    {
      accessor: 'sourceMarketId',
      header: MMS_SALES_PAGE_UI_PROPS.COLUMNS.MARKET_ID,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_MARKET(`${idx}`))}
          value={item?.sourceMarketId}
          tooltip
          variant="primary"
          copy={{ copyKey: `market-${item?.sourceOrderId}`, isCopy, onCopyClick }}
          className="w-[160px] text-[12px]"
        />
      ),
    },
    {
      accessor: 'sourceDeviceId',
      header: MMS_SALES_PAGE_UI_PROPS.COLUMNS.DEVICE_ID,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_DEVICE(`${idx}`))}
          value={item?.sourceDeviceId}
          tooltip
          variant="primary"
          copy={{ copyKey: `device-${item?.sourceOrderId}`, isCopy, onCopyClick }}
          className="w-[160px] text-[12px]"
        />
      ),
    },
    {
      accessor: 'sourceOrderId',
      header: MMS_SALES_PAGE_UI_PROPS.COLUMNS.SOURCE_ORDER_ID,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_SOURCE_ORDER_ID(`${idx}`))}
          value={item?.sourceOrderId}
          tooltip
          variant="primary"
          copy={{ copyKey: `order-${item?.sourceOrderId}`, isCopy, onCopyClick }}
          className="w-[150px] text-[12px]"
        />
      ),
    },
    {
      accessor: 'orderTotal',
      header: MMS_SALES_PAGE_UI_PROPS.COLUMNS.NET_TOTAL,
      sorting: true,
      render: ({ orderTotal, sourceOrderId }, idx) => (
        <Cell
          testId={tid(TID.COL_ORDER_TOTAL(`${idx}`))}
          value={orderTotal}
          format="currency"
        />
      ),
    },
    {
      accessor: 'orderGrossTotal',
      header: MMS_SALES_PAGE_UI_PROPS.COLUMNS.GROSS_TOTAL,
      sorting: true,
      render: ({ orderGrossTotal, sourceOrderId }, idx) => (
        <Cell
          testId={tid(TID.COL_GROSS_TOTAL(`${idx}`))}
          value={orderGrossTotal}
          format="currency"
        />
      ),
    },
    {
      accessor: 'orderDtm',
      header: MMS_SALES_PAGE_UI_PROPS.COLUMNS.ORDER_DTM,
      sorting: true,
      render: ({ orderDtm, sourceOrderId }, idx) => (
        <Cell
          testId={tid(TID.COL_ORDER_DTM(`${idx}`))}
          value={orderDtm}
          format="datetime"
          variant="muted"
        />
      ),
    },
    {
      accessor: 'createdOn',
      header: MMS_SALES_PAGE_UI_PROPS.COLUMNS.CREATED_ON,
      sorting: true,
      render: ({ createdOn, sourceOrderId }, idx) => (
        <div className="flex flex-col gap-0.5">
          <Cell
            testId={tid(TID.COL_CREATED_ON(`${idx}`))}
            value={createdOn}
            format="datetime"
            variant="muted"
          />
          <Cell
            value={createdOn}
            size="text-[10px]"
            variant="muted"
          >
            {getDateTimeAgo(createdOn)}
          </Cell>
        </div>
      ),
    },
  ];
